import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { ImportBranchDto, PortOfLadingDto, portOfLadingValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'

interface PortOfLadingMaintenanceAddEditDialogProps {
  initialValues: PortOfLadingDto
  isLoading?: boolean
  open: boolean
  onClose: () => void
  onSave: (values: PortOfLadingDto) => void
}

export default function PortOfLadingMaintenanceAddEditDialog({
  isLoading,
  initialValues,
  open,
  onClose,
  onSave
}: PortOfLadingMaintenanceAddEditDialogProps) {
  const [importBranches, setImportBranches] = useState<ImportBranchDto[]>([])
  const [isGettingImportBranches, setIsGettingImportBranches] = useState<boolean>(false)

  useEffect(() => {
    // Only get import branches when the user first opens the dialog.
    if (open && importBranches.length === 0) {
      setIsGettingImportBranches(true)
      controllers
        .getImportBranches()
        .then(response => {
          setImportBranches(response.value)
        })
        .finally(() => {
          setIsGettingImportBranches(false)
        })
    }
  }, [open, importBranches.length])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnChange
        validateOnBlur
        validationSchema={portOfLadingValidationSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Ports of Lading Maintenance</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(touched.cityProvince && errors.cityProvince)}
                      helperText={touched.cityProvince && errors.cityProvince}
                      label='Port of Lading'
                      name='cityProvince'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.cityProvince}
                    />
                  </Grid>

                  {isGettingImportBranches && (
                    <Grid item xs={12} container alignItems='center' justifyContent='center'>
                      <Grid>
                        <CircularProgress />
                      </Grid>
                    </Grid>
                  )}

                  {!isGettingImportBranches &&
                    importBranches.map(importBranch => (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.importBranches?.some(
                                valueImportBranch => valueImportBranch.importBranchGuid === importBranch.importBranchGuid
                              )}
                              disabled={isLoading || isSubmitting}
                              // Using handleChange results in the formik error " isHQLocation: "isHQLocation must be a `boolean` type, but the final value was: `[]`." "
                              onChange={e => {
                                if (
                                  values.importBranches?.some(
                                    valueImportBranch => valueImportBranch.importBranchGuid === importBranch.importBranchGuid
                                  )
                                ) {
                                  // If the import branch already exists in values.importBranches, remove it
                                  setFieldValue(
                                    'importBranches',
                                    values.importBranches.filter(
                                      valueImportBranch => valueImportBranch.importBranchGuid !== importBranch.importBranchGuid
                                    )
                                  )
                                } else {
                                  // Otherwise add it
                                  setFieldValue('importBranches', [...(values.importBranches ?? []), importBranch])
                                }
                              }}
                              // value={values.isHQLocation}
                            />
                          }
                          label={importBranch.name}
                        />
                      </Grid>
                    ))}
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <ExplanationAccordion variant='outlined'>
                    ||All fields are required. || Port of Lading entry should be formatted as City, Province. ||
                  </ExplanationAccordion>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={isLoading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
